import React, { createContext, useCallback, useContext, useState } from 'react';

// Tiny toast system — replaces window.alert for action feedback.
// Usage: const toast = useToast(); toast.success('Listed!'); toast.error(err.message);

const ToastContext = createContext(null);

let _id = 0;

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);

    const push = useCallback((kind, message) => {
        const id = ++_id;
        setToasts((prev) => [...prev, { id, kind, message }]);
        // Auto-dismiss after 3.5s (errors stick a bit longer).
        const ttl = kind === 'error' ? 6000 : 3500;
        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
        }, ttl);
        return id;
    }, []);

    const value = {
        success: (msg) => push('success', msg),
        error: (msg) => push('error', msg),
        info: (msg) => push('info', msg),
    };

    return (
        <ToastContext.Provider value={value}>
            {children}
            <div className="broker-toast-stack" aria-live="polite">
                {toasts.map((t) => (
                    <div key={t.id} className={`broker-toast broker-toast-${t.kind}`}>
                        <span className="broker-toast-icon">
                            {t.kind === 'success' ? '✓' : t.kind === 'error' ? '!' : 'i'}
                        </span>
                        <span className="broker-toast-message">{t.message}</span>
                        <button
                            className="broker-toast-close"
                            onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
                            aria-label="Dismiss"
                        >
                            ×
                        </button>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
}

export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        // Fall back to console + window.alert so calls don't crash if provider is missing.
        return {
            success: (m) => console.log('[toast]', m),
            error: (m) => { console.error('[toast]', m); window.alert(m); },
            info: (m) => console.log('[toast]', m),
        };
    }
    return ctx;
}
